import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import MainLogo from "../../assets/image/logo-image.png";

const KcePaymentStatus = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [paymentData, setPaymentData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setPaymentData(null);
      setErrorMsg("");
      setLoading(false);
      return;
    }

    if (query.length < 3) {
      setPaymentData(null);
      setErrorMsg("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_END_POINT}/kce-payment-status`,
          { searchQuery: query }
        );

        if (response.data && response.data.success) {
          setPaymentData(response.data);
          setErrorMsg("");
        } else {
          setPaymentData(null);
          setErrorMsg(response.data?.message || "No payment details found.");
        }
      } catch (err) {
        console.error("Error fetching payment status:", err);
        setPaymentData(null);
        const msg =
          err.response?.data?.message ||
          "Failed to fetch payment status. Please try again.";
        setErrorMsg(msg);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleExpand = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "captured":
        return "bg-success text-white";
      case "authorized":
        return "bg-primary text-white";
      case "failed":
        return "bg-danger text-white";
      case "refunded":
        return "bg-warning text-dark";
      case "created":
        return "bg-info text-dark";
      default:
        return "bg-secondary text-white";
    }
  };

  return (
    <div className="container-fluid min-vh-100 bg-light py-5">
      <div className="container">
        {/* Header Section */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-10 text-center">
            <div className="bg-white p-4 rounded-4 shadow-sm border">
              <img
                src={MainLogo}
                alt="Logo"
                style={{ height: "65px", objectFit: "contain" }}
                className="mb-3"
              />
              <h2 className="fw-bold text-dark mb-1">
                KCE Razorpay Payment Status
              </h2>
              <p className="text-muted mb-0 fs-6">
                Type Barcode or Mobile Number to search Razorpay payment status
              </p>
            </div>
          </div>
        </div>

        {/* Search Input Section */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-8">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4">
                <div className="mb-0">
                  <label
                    htmlFor="searchQueryInput"
                    className="form-label fw-semibold text-secondary"
                  >
                    Barcode / Mobile Number
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      id="searchQueryInput"
                      className="form-control form-control-lg rounded-3 shadow-none fs-6 pe-5"
                      placeholder="Type Barcode (e.g. VIS10023) or Mobile Number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                    <div
                      className="position-absolute top-50 end-0 translate-middle-y me-3 pointer-events-none d-flex align-items-center"
                      style={{ color: "#6c757d" }}
                    >
                      {loading ? (
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="currentColor"
                          className="bi bi-search"
                          viewBox="0 0 16 16"
                        >
                          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <div className="alert alert-danger rounded-3 mt-3 mb-0" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {errorMsg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Razorpay Payments Table Section */}
        {paymentData && paymentData.payments && (
          <div className="row justify-content-center">
            <div className="col-md-11">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-header bg-white p-3 border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold text-dark mb-0">
                    Razorpay Payment Records ({paymentData.payments.length})
                  </h5>
                </div>
                <div className="card-body p-0">
                  {paymentData.payments.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Payment ID</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>Date</th>
                            <th>Razorpay Dashboard Link</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentData.payments.map((p, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td>
                                  <span className="fw-bold text-primary">
                                    {p.payment_id}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`badge px-2 py-1 text-uppercase ${getStatusBadgeClass(
                                      p.status
                                    )}`}
                                  >
                                    {p.status}
                                  </span>
                                </td>
                                <td>
                                  <span className="fw-semibold">
                                    {p.currency === "INR" ? "₹" : p.currency}{" "}
                                    {p.amount?.toLocaleString("en-IN")}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-capitalize">
                                    {p.method || "N/A"}
                                  </span>
                                </td>
                                <td>{p.contact || "N/A"}</td>
                                <td>
                                  <small className="text-muted">{p.email || "N/A"}</small>
                                </td>
                                <td>
                                  <small>
                                    {p.created_at
                                      ? moment.unix(p.created_at).format("DD MMM YYYY, hh:mm A")
                                      : "N/A"}
                                  </small>
                                </td>
                                <td>
                                  <a
                                    href={p.payment_details_urls}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-semibold"
                                  >
                                    Open Dashboard &#8599;
                                  </a>
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light border fw-semibold"
                                    onClick={() => toggleExpand(index)}
                                  >
                                    {expandedRow === index ? "Hide Details" : "View Details"}
                                  </button>
                                </td>
                              </tr>

                              {/* Expanded Row for Full Details & JSON */}
                              {expandedRow === index && (
                                <tr>
                                  <td colSpan="9" className="bg-light p-4">
                                    <div className="row g-3 mb-3">
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Order ID</small>
                                        <strong>{p.order_id || "N/A"}</strong>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Invoice ID</small>
                                        <strong>{p.invoice_id || "N/A"}</strong>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Fee / Tax</small>
                                        <strong>₹{p.fee} / ₹{p.tax}</strong>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Notes</small>
                                        <strong>
                                          {p.notes && Object.keys(p.notes).length > 0
                                            ? JSON.stringify(p.notes)
                                            : "None"}
                                        </strong>
                                      </div>
                                    </div>

                                    {p.status === "failed" && (
                                      <div className="alert alert-danger mb-3 py-2 px-3">
                                        <strong>Failure Reason:</strong>{" "}
                                        {p.error_description || p.error_reason || "Payment failed."}
                                        {p.error_code && <span> (Code: {p.error_code})</span>}
                                      </div>
                                    )}

                                    <div className="mb-2">
                                      <small className="text-muted d-block mb-1 fw-bold">
                                        Razorpay Direct Dashboard URL (payment_details_urls):
                                      </small>
                                      <a
                                        href={p.payment_details_urls}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary text-break"
                                      >
                                        {p.payment_details_urls}
                                      </a>
                                    </div>

                                    <div className="mt-3">
                                      <small className="text-muted d-block mb-1 fw-bold">
                                        Full Razorpay API Response (JSON):
                                      </small>
                                      <div
                                        className="p-3 bg-dark text-light rounded-3 overflow-auto style-scrollbar"
                                        style={{ maxHeight: "250px" }}
                                      >
                                        <pre className="m-0 text-success small">
                                          {JSON.stringify(p.all_details, null, 2)}
                                        </pre>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-4 text-center text-muted">
                      No Razorpay payment records found.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KcePaymentStatus;
